// Validates whether a string is well-formed Base64 (standard or URL-safe alphabet).

export interface Base64ValidationResult {
  valid: boolean;
  reason: string;
  variant?: 'standard' | 'url-safe';
}

const STANDARD_RE = /^[A-Za-z0-9+/]*={0,2}$/;
const URL_SAFE_RE = /^[A-Za-z0-9_-]*$/;

export function validateBase64(input: string): Base64ValidationResult {
  const value = input.trim();

  if (value.length === 0) {
    return { valid: false, reason: 'Input is empty.' };
  }

  const hasStandardChars = /[+/=]/.test(value);
  const hasUrlSafeChars = /[-_]/.test(value);

  if (hasStandardChars && hasUrlSafeChars) {
    return { valid: false, reason: 'Mixes standard ("+", "/", "=") and URL-safe ("-", "_") characters — not valid in either alphabet.' };
  }

  const variant: 'standard' | 'url-safe' = hasUrlSafeChars ? 'url-safe' : 'standard';

  if (variant === 'standard') {
    if (!STANDARD_RE.test(value)) {
      return { valid: false, reason: 'Contains characters outside the Base64 alphabet (A–Z, a–z, 0–9, "+", "/", "=" padding).' };
    }
    const padIndex = value.indexOf('=');
    if (padIndex !== -1 && !/^=*$/.test(value.slice(padIndex))) {
      return { valid: false, reason: '"=" padding must only appear at the end of the string.' };
    }
    if (value.length % 4 !== 0) {
      return { valid: false, reason: `Length (${value.length}) is not a multiple of 4 — standard Base64 must be padded to a multiple of 4 characters.` };
    }
  } else {
    if (!URL_SAFE_RE.test(value)) {
      return { valid: false, reason: 'Contains characters outside the URL-safe Base64 alphabet (A–Z, a–z, 0–9, "-", "_").' };
    }
  }

  try {
    const normalized = variant === 'url-safe'
      ? value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=')
      : value;
    atob(normalized);
  } catch {
    return { valid: false, reason: 'Failed to decode — the string is not valid Base64.' };
  }

  return { valid: true, reason: `Valid ${variant === 'url-safe' ? 'URL-safe' : 'standard'} Base64.`, variant };
}
